import { useDialog } from '@ucheba/ui/components/Dialog/bll'
import { IDialog } from '@ucheba/ui/components/Dialog/types'
import { useDispatch, useSelector } from 'react-redux'
import { probabilitiesSelectors, probabilitiesThunks } from '@ucheba/store/probabilities'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { profileSelectors, profileThunks } from '@ucheba/store/profile'
import { authSelectors } from '@ucheba/store/auth'
import { useRouter } from 'next/router'
import { removeQueryParameters, stringifyUrl } from '@ucheba/utils/helpers/router'
import {
  personalAchievementsSelectors,
  personalAchievementsThunks,
} from '@ucheba/store/personalAchievements'
import { getParsedCookies } from '@ucheba/utils/helpers/core'
import { authCookieToken } from '@ucheba/utils/constants/core'
import { EAssessmentResults } from './components/OddsAssessmentResults/types'

interface IUseOddsAssessment {
  (
    programId: number,
    selectEgeDialogId?: string,
    resultDialogId?: string
  ): {
    selectEgeDialog: IDialog
    resultsDialog: IDialog
    probability: keyof typeof EAssessmentResults | null
    hasCurrentProbability: boolean
    handleSubmit: () => void
    checkAuth: () => void
    hasOpenParams: boolean
    getAchievements: () => void
    onOpenResultsDialog: () => Promise<any>
    onClose: () => void
    onCloseSelectEgeDialog: () => void
    handleCloseSelectEgeDialog: () => void
  }
}

export const useOddsAssessment: IUseOddsAssessment = (
  programId,
  selectEgeDialogId,
  resultDialogId
) => {
  const selectEgeDialog = useDialog(selectEgeDialogId || 'oddsAssessmentSelectEgeId')
  const resultsDialog = useDialog(resultDialogId || 'oddsAssessmentResultsId')

  const currentProbability = useSelector(probabilitiesSelectors.currentProbability)
  const dispatch = useDispatch()
  const profile = useSelector(profileSelectors.entity)

  // сделан ли запрос
  const [didSubmit, setDidSubmit] = useState(false)
  const isAuth = useSelector(authSelectors.isAuth)
  const { push, asPath, query, replace } = useRouter()

  const achievementsItems = useSelector(personalAchievementsSelectors.selectAll)

  const getAchievements = useCallback(() => {
    if (!achievementsItems.length) {
      dispatch(personalAchievementsThunks.fetch({}))
    }
  }, [achievementsItems.length])

  const checkAuth = useCallback(() => {
    const parsedCookie = getParsedCookies()
    const tokenString = parsedCookie[authCookieToken]

    if (!isAuth || !tokenString) {
      push(
        stringifyUrl('/auth', {
          include: {
            redirect: `${removeQueryParameters(asPath)}?showOddsStep1=1`,
          },
        })
      )
    }
  }, [isAuth, asPath, push])

  // есть ли оценка шансов
  const hasCurrentProbability = useMemo(() => {
    if (!isAuth) return false

    return !!currentProbability
  }, [currentProbability, isAuth])

  const exams = useMemo(() => {
    return (
      profile?.enrolleeInfo?.exams?.map((exam) => ({
        examId: exam.exam.id,
        score: exam.score,
      })) || []
    )
  }, [profile])

  const achievements = useMemo(() => {
    return profile?.enrolleeInfo?.achievements || []
  }, [profile])

  // при загрузке страницы делаем пост запрос чтоб получить программы в оценке шансов
  const handleSubmit = useCallback(async () => {
    if (hasCurrentProbability && profile && !didSubmit) {
      await dispatch(
        probabilitiesThunks.post({
          data: {
            type: 'program',
            referId: programId,
            scores: exams,
            achievementIds: achievements.map((el) => el.id),
            readyToExam: true,
          },
        })
      )

      setDidSubmit(true)
    }
  }, [
    hasCurrentProbability,
    achievements,
    exams,
    programId,
    dispatch,
    profile,
    didSubmit,
  ])

  // резуьтат оценки шансов
  const probability = useMemo(() => {
    if (typeof currentProbability !== 'number' && !currentProbability) return null

    if (currentProbability > 90) {
      return EAssessmentResults.high
    }
    if (currentProbability > 50) {
      return EAssessmentResults.middle
    }

    return EAssessmentResults.low
  }, [currentProbability])

  const hasOpenParams = useMemo(() => {
    return !!query.showOddsStep1
  }, [query])

  const onOpenResultsDialog = useCallback(async (): Promise<any> => {
    const parsedCookie = getParsedCookies()
    const tokenString = parsedCookie[authCookieToken]

    if (!isAuth || !tokenString) {
      checkAuth()
    } else {
      resultsDialog.openDialog()
      if (!profile) {
        try {
          await dispatch(profileThunks.get({}))
        } catch (e) {
          console.log(e)
        }
      }
    }
  }, [checkAuth, dispatch, isAuth, profile, resultsDialog])

  const onClose = useCallback(() => {
    if (resultsDialog.isActive) {
      resultsDialog.closeDialog()

      if (query.showOddsStep1) {
        replace(
          stringifyUrl(removeQueryParameters(asPath), {
            exclude: ['showOddsStep1'],
          }),
          undefined,
          {
            shallow: true,
          }
        )
      }
    }
  }, [asPath, query.showOddsStep1, replace, resultsDialog])

  const onCloseSelectEgeDialog = useCallback(() => {
    if (selectEgeDialog.isActive) {
      selectEgeDialog.closeDialog()

      if (query.showOddsStep1) {
        replace(
          stringifyUrl(removeQueryParameters(asPath), {
            exclude: ['showOddsStep1'],
          }),
          undefined,
          {
            shallow: true,
          }
        )
      }
    }
  }, [asPath, query.showOddsStep1, replace, resultsDialog])

  const handleCloseSelectEgeDialog = useCallback(() => {
    resultsDialog.openDialog()

    selectEgeDialog.closeDialog()
  }, [resultsDialog, selectEgeDialog])

  return {
    selectEgeDialog,
    resultsDialog,
    onOpenResultsDialog,
    probability,
    hasCurrentProbability,
    handleSubmit,
    checkAuth,
    hasOpenParams,
    getAchievements,
    onClose,
    onCloseSelectEgeDialog,
    handleCloseSelectEgeDialog,
  }
}
